import { useState } from 'react'
import { useParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { Spinner, Avatar } from '../../components/ui.jsx'
import { ConfirmarBorrado } from '../../components/ConfirmarBorrado.jsx'

export function Miembros() {
  const { slug } = useParams()
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [nivelNuevo, setNivelNuevo] = useState(30)
  const [aQuitar, setAQuitar] = useState(null)

  const q = useQuery({ queryKey: ['members', slug], queryFn: () => api.members(slug) })
  const resultados = useQuery({
    queryKey: ['userSearch', slug, busca],
    queryFn: () => api.searchUsers(slug, busca),
    enabled: busca.trim().length >= 2,
  })
  const refrescar = () => qc.invalidateQueries({ queryKey: ['members', slug] })

  const anadir = useMutation({
    mutationFn: (userId) => api.addMember(slug, userId, nivelNuevo),
    onSuccess: () => { setBusca(''); refrescar() },
  })
  const cambiar = useMutation({
    mutationFn: ({ userId, nivel }) => api.setMemberLevel(slug, userId, nivel),
    onSuccess: refrescar,
  })
  const quitar = useMutation({
    mutationFn: (userId) => api.removeMember(slug, userId),
    onSuccess: () => { setAQuitar(null); refrescar() },
  })

  if (q.isPending) return <Spinner />
  if (q.isError) return <div className="alert error">{q.error.body?.detail ?? 'GitLab no responde.'}</div>

  const err = anadir.error ?? cambiar.error ?? quitar.error
  const nombreNivel = (n) => q.data.niveles.find((x) => x.valor === n)?.nombre ?? n

  return (
    <>
      <h1 className="h1">Miembros</h1>
      <p className="lede">
        Los permisos viven en GitLab, no en la consola: esto escribe allí. Con nivel{' '}
        <strong>Maintainer</strong> o superior se es administrador del proyecto en la consola —
        desplegar, funciones, variables y baja.
      </p>

      {err && <div className="alert error" style={{ marginBottom: 16 }}>{err.body?.detail ?? err.message}</div>}

      <div className="card card-pad" style={{ marginBottom: 18 }}>
        <div className="field-label">Añadir a alguien</div>
        <div className="toolbar">
          <input className="input" placeholder="buscar por nombre o usuario…" value={busca}
                 style={{ flex: 1, minWidth: 220 }} onChange={(e) => setBusca(e.target.value)} />
          <select className="input" value={nivelNuevo} onChange={(e) => setNivelNuevo(Number(e.target.value))}>
            {q.data.niveles.map((n) => (
              <option key={n.valor} value={n.valor}>{n.nombre}{n.nota ? ` — ${n.nota}` : ''}</option>
            ))}
          </select>
        </div>
        {resultados.data?.length > 0 && (
          <div className="card" style={{ marginTop: 10 }}>
            {resultados.data.map((u) => (
              <div key={u.id} className="menu-item" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <Avatar src={u.avatar} name={u.nombre} />
                <span>{u.nombre} <span style={{ color: 'var(--fg-3)' }}>@{u.username}</span></span>
                <button className="btn sm primary" style={{ marginLeft: 'auto' }}
                        disabled={anadir.isPending} onClick={() => anadir.mutate(u.id)}>
                  Añadir como {nombreNivel(nivelNuevo)}
                </button>
              </div>
            ))}
          </div>
        )}
        {busca.trim().length >= 2 && resultados.data?.length === 0 && (
          <div className="field-hint">Nadie con ese nombre en GitLab.</div>
        )}
      </div>

      <div className="card"><div className="table-scroll">
        <table className="t">
          <thead><tr><th>Persona</th><th>Nivel</th><th>Origen</th><th /></tr></thead>
          <tbody>
            {q.data.miembros.map((m) => (
              <tr key={m.id}>
                <td>
                  <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
                    <Avatar src={m.avatar} name={m.nombre} />
                    <span>{m.nombre} <span style={{ color: 'var(--fg-3)' }}>@{m.username}</span></span>
                    {m.esAdmin && <span className="badge done"><span className="dot" />admin</span>}
                  </div>
                </td>
                <td>
                  {m.directo ? (
                    <select className="input" value={m.nivel} disabled={cambiar.isPending}
                            onChange={(e) => cambiar.mutate({ userId: m.id, nivel: Number(e.target.value) })}>
                      {q.data.niveles.map((n) => <option key={n.valor} value={n.valor}>{n.nombre}</option>)}
                    </select>
                  ) : nombreNivel(m.nivel)}
                </td>
                <td style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                  {m.directo ? 'del proyecto' : 'heredado del grupo'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {m.directo
                    ? <button className="btn sm danger" onClick={() => setAQuitar(m)}>Quitar</button>
                    : <span className="hint" style={{ marginLeft: 0 }}>se quita en el grupo</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div></div>

      <ConfirmarBorrado
        abierto={!!aQuitar} nombre={aQuitar?.username ?? ''} titulo="Quitar del proyecto"
        etiquetaBoton="Quitar" ocupado={quitar.isPending}
        onCancelar={() => setAQuitar(null)}
        onConfirmar={() => quitar.mutate(aQuitar.id)}
      >
        <p style={{ marginTop: 0 }}>
          Se quita a <strong>{aQuitar?.nombre}</strong> del repositorio en GitLab, y con ello pierde
          el acceso a este proyecto en la consola.
        </p>
        <p style={{ marginBottom: 0 }}>
          Su sesión abierta deja de ver el proyecto en cuanto vuelva a entrar; para cortarla ya, usa
          <strong> Sesiones</strong> en el menú de administración.
        </p>
      </ConfirmarBorrado>
    </>
  )
}
